import type { Config, DeepPartial, GlobalConfig, SpeciesConfig, Wall } from './state';
import type { Species } from './types';

const SPECIES_KEY = {
    minnow: 'n_minnow',
    tetra: 'n_tetra',
    bass: 'n_bass',
    pike: 'n_pike',
} as const satisfies Record<Species, `n_${Species}`>;

const SPECIES_LIST: readonly Species[] = ['minnow', 'tetra', 'bass', 'pike'];

const SCENE_KEYS: readonly string[] = [
    'seed',
    'n_minnow',
    'n_tetra',
    'n_bass',
    'n_pike',
    'agg',
    'shoal',
    'walls',
];

const COUNT_MAX = 60;
const AGG_MAX = 3;
const SHOAL_MAX = 2;
const WALLS_MAX = 256;

function packWalls(walls: readonly Wall[]): string {
    if (walls.length === 0) return '';
    const arr = new Int16Array(walls.length * 4);
    let idx = 0;
    for (const w of walls) {
        arr[idx] = clamp16(w[0]);
        arr[idx + 1] = clamp16(w[1]);
        arr[idx + 2] = clamp16(w[2]);
        arr[idx + 3] = clamp16(w[3]);
        idx += 4;
    }
    const bytes = new Uint8Array(arr.buffer);
    let s = '';
    for (const b of bytes) s += String.fromCharCode(b);
    return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function clamp16(v: number): number {
    if (!Number.isFinite(v)) return 0;
    const r = Math.round(v);
    if (r < -32768) return -32768;
    if (r > 32767) return 32767;
    return r;
}

function unpackWalls(packed: string): Wall[] {
    if (packed === '') return [];
    let b64 = packed.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4 !== 0) b64 += '=';
    let raw: string;
    try {
        raw = atob(b64);
    } catch {
        return [];
    }
    if (raw.length === 0 || raw.length % 8 !== 0) return [];
    const buf = new ArrayBuffer(raw.length);
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i) & 0xff;
    const arr = new Int16Array(buf);
    const walls: Wall[] = [];
    for (let i = 0; i + 4 <= arr.length && walls.length < WALLS_MAX; i += 4) {
        walls.push([arr[i] ?? 0, arr[i + 1] ?? 0, arr[i + 2] ?? 0, arr[i + 3] ?? 0]);
    }
    return walls;
}

export function encodeScene(cfg: Config, seed: string): string {
    const params = new URLSearchParams();
    params.set('seed', seed);
    for (const sp of SPECIES_LIST) {
        params.set(SPECIES_KEY[sp], String(cfg.species[sp].count));
    }
    params.set('agg', formatNumber(cfg.global.aggression));
    params.set('shoal', formatNumber(cfg.global.shoaling));
    if (cfg.walls.length > 0) {
        params.set('walls', packWalls(cfg.walls));
    }
    return params.toString();
}

function formatNumber(n: number): string {
    if (!Number.isFinite(n)) return '0';
    return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(3)));
}

export interface DecodedScene {
    cfg: DeepPartial<Config>;
    seed: string;
}

export function decodeScene(qs: string): DecodedScene | null {
    const cleaned = qs.startsWith('?') ? qs.slice(1) : qs;
    if (cleaned === '') return null;
    const params = new URLSearchParams(cleaned);

    let recognized = false;
    for (const k of SCENE_KEYS) {
        if (params.has(k)) {
            recognized = true;
            break;
        }
    }
    if (!recognized) return null;

    const cfg: DeepPartial<Config> = {};
    const speciesPartial: Partial<Record<Species, Partial<SpeciesConfig>>> = {};
    for (const sp of SPECIES_LIST) {
        const v = params.get(SPECIES_KEY[sp]);
        if (v === null) continue;
        const n = Number(v);
        if (Number.isFinite(n) && n >= 0) {
            speciesPartial[sp] = { count: Math.min(COUNT_MAX, Math.floor(n)) };
        }
    }
    if (Object.keys(speciesPartial).length > 0) {
        cfg.species = speciesPartial;
    }

    const globalPartial: Partial<GlobalConfig> = {};
    const agg = params.get('agg');
    if (agg !== null) {
        const n = Number(agg);
        if (Number.isFinite(n) && n >= 0) globalPartial.aggression = Math.min(AGG_MAX, n);
    }
    const shoal = params.get('shoal');
    if (shoal !== null) {
        const n = Number(shoal);
        if (Number.isFinite(n) && n >= 0) globalPartial.shoaling = Math.min(SHOAL_MAX, n);
    }
    if (Object.keys(globalPartial).length > 0) {
        cfg.global = globalPartial;
    }

    const walls = params.get('walls');
    if (walls !== null) {
        cfg.walls = unpackWalls(walls);
    }

    const seed = params.get('seed') ?? '';
    return { cfg, seed };
}

export function buildShareUrl(origin: string, pathname: string, qs: string): string {
    const path = pathname === '' ? '/' : pathname;
    return `${origin}${path}?${qs}`;
}
